import { ReactNode } from 'react';
import { ButtonProps } from '../Button';

/**
 * Configuration for an action button within the StylovyzeCard
 */
export interface StylovyzeCardButton {
	/** Button text label */
	title: string;

	/** Optional icon element to display in button */
	icon?: ReactNode;

	/** Click handler for button action */
	onClick: () => void;

	/** Cypress testing attribute for button */
	cy?: string;

	/** Additional button props (excludes onClick to prevent conflicts) */
	props?: Omit<ButtonProps, 'onClick'>;
}

/**
 * Props interface for the StylovyzeCard component
 */
export interface StylovyzeCardProps {
	/**
	 * Main title text displayed in the card (required)
	 */
	topText: React.ReactNode | string;

	/**
	 * Color for the top accent bar of the card
	 * @default "#007096"
	 */
	topColor?: string;

	/**
	 * Main count/metric value to display
	 * Can be number for calculations or string for text display
	 */
	countText?: number | string;

	/**
	 * Subtitle text displayed below the main title
	 */
	topSubText?: React.ReactNode | string;

	/**
	 * Additional footer content displayed below the main card content
	 */
	secondaryFooter?: React.ReactNode | string;

	/**
	 * Footer text aligned to the left
	 * Footer divider only appears when either bottomTextLeft or bottomTextRight is provided
	 */
	bottomTextLeft?: React.ReactNode | string;

	/**
	 * Footer text aligned to the right
	 * Footer divider only appears when either bottomTextLeft or bottomTextRight is provided
	 */
	bottomTextRight?: React.ReactNode | string;

	/**
	 * Position of the title text relative to the count
	 * @default "right"
	 */
	titlePosition?: 'right' | 'above';

	/**
	 * Card height in pixels (converted to rem internally)
	 * @default 120
	 */
	height?: number;

	/**
	 * Enable full height for workspace dashlet usage
	 * @default false
	 */
	fullHeight?: boolean;

	/**
	 * Action button configuration
	 * When provided, displays a button with custom styling and click handler
	 */
	button?: StylovyzeCardButton;

	/**
	 * Format for displaying the count value
	 * - 'number': Display as provided
	 * - 'time': Convert minutes to days/hours/minutes format (only works with numeric countText)
	 * @default 'number'
	 */
	countFormat?: 'number' | 'time';

	/**
	 * Cypress testing attribute for the entire component
	 */
	dataCy?: string;
}

/**
 * Props for the internal FormatNumber component
 * @internal
 */
export interface FormatNumberProps {
	count?: number | string;
	format: StylovyzeCardProps['countFormat'];
}
